import React from "react";

/**
 * Designing & Estimation illustration — blueprint sheet with measurements,
 * calculator, and chart elements. Pure SVG, matches dark cinematic theme.
 */
const DesignIllustration = () => (
  <svg
    viewBox="0 0 600 360"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid slice"
    style={{ width: "100%", height: "100%", display: "block" }}
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="ds-bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#1a1d3a" />
        <stop offset="50%" stopColor="#2d2660" />
        <stop offset="100%" stopColor="#1e1547" />
      </linearGradient>
      <linearGradient id="ds-blueprint" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#1e3a8a" />
        <stop offset="100%" stopColor="#15296b" />
      </linearGradient>
      <linearGradient id="ds-purple" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#a78bfa" />
        <stop offset="100%" stopColor="#7c3aed" />
      </linearGradient>
      <linearGradient id="ds-violet" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#8a5cf6" />
        <stop offset="100%" stopColor="#5b21b6" />
      </linearGradient>
      <filter id="ds-glow" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="4" />
      </filter>
    </defs>

    {/* Background */}
    <rect width="600" height="360" fill="url(#ds-bg)" />

    {/* Grid pattern */}
    <g opacity="0.18" stroke="#a78bfa" strokeWidth="0.5">
      {Array.from({ length: 15 }).map((_, i) => (
        <line key={`v${i}`} x1={i * 40} y1="0" x2={i * 40} y2="360" />
      ))}
      {Array.from({ length: 10 }).map((_, i) => (
        <line key={`h${i}`} x1="0" y1={i * 40} x2="600" y2={i * 40} />
      ))}
    </g>

    {/* Blob behind blueprint */}
    <circle cx="500" cy="60" r="100" fill="#8a5cf6" opacity="0.25" filter="url(#ds-glow)" />
    <circle cx="80" cy="320" r="80" fill="#22d3ee" opacity="0.18" filter="url(#ds-glow)" />

    {/* Blueprint sheet — main focal element */}
    <g transform="translate(120, 70)">
      <rect width="360" height="230" rx="8" fill="url(#ds-blueprint)" stroke="#a78bfa" strokeWidth="1.2" opacity="0.95" />

      {/* Blueprint floor plan lines */}
      <g stroke="#a78bfa" strokeWidth="1.5" fill="none" opacity="0.7">
        {/* outer walls */}
        <rect x="40" y="40" width="280" height="150" />
        {/* inner walls dividing rooms */}
        <line x1="160" y1="40" x2="160" y2="120" />
        <line x1="160" y1="120" x2="320" y2="120" />
        <line x1="40" y1="120" x2="100" y2="120" />
        {/* door swing */}
        <path d="M 100 120 A 20 20 0 0 0 120 100" />
        <line x1="100" y1="120" x2="120" y2="120" strokeDasharray="2 2" />
      </g>

      {/* Dimension lines */}
      <g stroke="#22d3ee" strokeWidth="1" opacity="0.85">
        <line x1="40" y1="20" x2="320" y2="20" />
        <line x1="40" y1="14" x2="40" y2="26" />
        <line x1="320" y1="14" x2="320" y2="26" />
        <line x1="20" y1="40" x2="20" y2="190" />
        <line x1="14" y1="40" x2="26" y2="40" />
        <line x1="14" y1="190" x2="26" y2="190" />
      </g>

      {/* Dimension labels */}
      <text x="180" y="14" fill="#22d3ee" fontSize="9" textAnchor="middle" fontFamily="monospace">12500 mm</text>
      <text x="8" y="118" fill="#22d3ee" fontSize="9" textAnchor="middle" transform="rotate(-90 8 118)" fontFamily="monospace">6800 mm</text>

      {/* Title block */}
      <rect x="240" y="195" width="100" height="28" fill="rgba(167, 139, 250, 0.15)" stroke="#a78bfa" strokeWidth="0.8" opacity="0.8" />
      <text x="290" y="210" fill="#fff" fontSize="8" textAnchor="middle" fontFamily="Poppins, sans-serif" fontWeight="700">PLAN · A-101</text>
      <text x="290" y="219" fill="#a78bfa" fontSize="6" textAnchor="middle" fontFamily="Poppins, sans-serif">SCALE 1:100</text>
    </g>

    {/* Floating calculator card (bottom-left) */}
    <g transform="translate(50, 220)">
      <rect width="100" height="110" rx="10" fill="rgba(20, 16, 50, 0.92)" stroke="#a78bfa" strokeWidth="1.2" />
      <rect x="8" y="8" width="84" height="20" rx="3" fill="#0a0e1f" />
      <text x="86" y="22" fill="#22d3ee" fontSize="11" textAnchor="end" fontFamily="monospace" fontWeight="700">$24,500</text>
      {/* Calc buttons */}
      {[0, 1, 2, 3].map((row) => (
        [0, 1, 2].map((col) => (
          <rect
            key={`${row}-${col}`}
            x={8 + col * 30}
            y={36 + row * 18}
            width="26"
            height="14"
            rx="2"
            fill={row === 0 && col === 2 ? "url(#ds-purple)" : "rgba(167,139,250,0.18)"}
          />
        ))
      ))}
    </g>

    {/* Floating chart card (top-right corner) */}
    <g transform="translate(450, 230)">
      <rect width="120" height="80" rx="10" fill="rgba(20, 16, 50, 0.92)" stroke="#22d3ee" strokeWidth="1.2" />
      <text x="10" y="20" fill="#fff" fontSize="9" fontWeight="700" fontFamily="Poppins, sans-serif">COST TREND</text>
      {/* Bar chart */}
      {[24, 38, 30, 50, 42, 60].map((h, i) => (
        <rect
          key={i}
          x={10 + i * 17}
          y={70 - h}
          width="12"
          height={h}
          rx="1.5"
          fill={i === 5 ? "url(#ds-purple)" : "rgba(167,139,250,0.5)"}
        />
      ))}
    </g>

    {/* Accent dots */}
    <circle cx="100" cy="100" r="3" fill="#22d3ee">
      <animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite" />
    </circle>
    <circle cx="540" cy="180" r="3" fill="#a78bfa">
      <animate attributeName="opacity" values="0.4;1;0.4" dur="2.5s" repeatCount="indefinite" />
    </circle>
  </svg>
);

export default DesignIllustration;
